import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import { BUSY, DISABLED, FOCUS_RING, HOVER_OUTLINE, HOVER_QUIET, HOVER_SOLID } from './states';

/**
 * Primitive de bouton (audit UX/UI passe 2, B1 : « aucun composant Button…
 * réutilisable n'existe »). Un seul point de vérité pour les styles de
 * bouton, alors qu'ils étaient jusqu'ici redéfinis indépendamment dans
 * chaque écran (`buttonBase` dans CookMode.tsx, `actionButtonClass` dans
 * CookLogForm.tsx, classes recopiées en dur dans App.tsx, RecipeForm.tsx,
 * RecipeDetail.tsx, DataBackup.tsx, ImportUrlForm.tsx, ImportPasteForm.tsx).
 *
 * ### Cinq variantes, et pourquoi il en fallait cinq (refonte éditoriale)
 *
 * Trois variantes ne suffisaient pas, et le manque se voyait : la fiche
 * recette alignait « Ajouter une réalisation », « À cuisiner », « Favori »,
 * « Modifier » et « Archiver » en cinq boutons `secondary` — cinq rectangles
 * cerclés, de même poids, sous un « Cuisiner » plein qui était pourtant la
 * seule action que quelqu'un venait faire. Faute d'un rang intermédiaire,
 * tout ce qui n'était pas l'action principale devenait une bordure de plus.
 *
 * | variante    | emploi | poids |
 * | ----------- | ------ | ----- |
 * | `primary`   | l'action que la page existe pour faire. **Une seule par écran.** | plein |
 * | `secondary` | une action attendue mais pas centrale (Annuler, Précédent) | contour |
 * | `quiet`     | une action de service (Modifier, Archiver, Partager) | rien, jusqu'au survol |
 * | `ghost`     | un lien déguisé en action (retour, « voir tout ») | souligné |
 * | `danger`    | une action destructrice confirmée | contour rouge |
 *
 * `quiet` est le rang qui manquait. `danger` existait de fait — les écrans le
 * peignaient à la main en `text-danger` — sans jamais porter les états.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'ghost' | 'danger';
export type ButtonSize = 'default' | 'compact' | 'large' | 'inline' | 'icon';

// `transition` (pas seulement `transition-colors`) : couvre aussi `transform`,
// pour le léger tassement au clic ci-dessous (`active:scale-[0.97]`) —
// micro-interaction manquante sur la quasi-totalité des actions de
// l'application (audit UX/UI passe 2, I5 : « aucune transition ni
// confirmation visuelle au-delà du changement d'état brut »). Un seul point
// de vérité (`Button`) plutôt que de la répéter action par action ; respecte
// `prefers-reduced-motion` comme le reste de l'application (index.css).
// Les six états de la table (US-25, `states.ts`) : repos porté par la
// variante, survol idem, focus, pressé (`active:scale`), désactivé, en cours
// (`aria-busy`).
//
// Le mouvement vient du design system depuis la refonte éditoriale, il n'est
// plus hérité de la durée par défaut de Tailwind (150 ms, `ease-in-out`).
//
// `duration-(--duration-quick)` et non `duration-quick` : Tailwind v4 n'a pas
// de namespace `--duration-*`, seulement `--ease-*`. Une classe `duration-quick`
// se compile en **rien du tout** — exactement le défaut silencieux décrit par
// `classesCompilees.test.ts` (une classe absente ne casse rien, elle ne fait
// simplement rien). La syntaxe de variable CSS, elle, produit la règle. Le
// test a d'ailleurs été étendu aux préfixes `duration-` et `ease-` pour que
// la prochaine tentative échoue au lieu de passer inaperçue.
const BASE =
  `inline-flex items-center justify-center gap-2 rounded-control font-medium tracking-[-0.005em] active:scale-[0.97] ${FOCUS_RING} ${DISABLED} ${BUSY} transition duration-(--duration-quick) ease-entrance`;

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: `bg-primary text-primary-foreground shadow-card ${HOVER_SOLID}`,
  // Contour conforme WCAG 1.4.11 (US-24) : un bouton secondaire n'a que sa
  // bordure pour se signaler.
  secondary: `border border-border-interactive text-foreground ${HOVER_OUTLINE} ${HOVER_QUIET}`,
  // Ni fond ni bordure au repos ; une surface apparaît au survol. C'est le
  // rang des actions de service, celles qu'on doit pouvoir trouver sans
  // qu'elles réclament l'attention à chaque coup d'œil.
  quiet: `text-foreground-secondary hover:text-foreground ${HOVER_QUIET}`,
  // Un lien qui se comporte en bouton : souligné, parce que c'est ce qui
  // distingue un lien d'un mot en couleur.
  ghost: 'text-foreground underline decoration-border-strong underline-offset-4 font-normal hover:decoration-primary hover:text-primary',
  // Le rouge est sur le texte et la bordure, jamais en aplat : un aplat rouge
  // dans une page attire l'œil avant l'action que la page sert à faire.
  danger: 'border border-danger text-danger hover:bg-danger-bg',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  default: 'min-h-touch px-4 text-sm',
  compact: 'min-h-touch-min px-3 text-sm',
  // L'action principale d'un écran. `text-base` et non `text-lg` : à 18 px un
  // libellé de bouton concurrence les titres de section.
  large: 'min-h-touch px-6 text-base font-semibold',
  // Sans padding horizontal : boutons « lien de retour » (← Bibliothèque, ← Fiche recette).
  inline: 'min-h-touch-min text-sm',
  // Carré, cible tactile minimale (§4 CLAUDE.md) : convention pour les
  // boutons iconographiques (audit UX/UI passe 2, F4).
  icon: 'size-touch p-0 text-base font-semibold',
};

export function buttonClasses(variant: ButtonVariant = 'secondary', size: ButtonSize = 'default', className = ''): string {
  return `${BASE} ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${className}`.trim();
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = 'secondary',
  size = 'default',
  className = '',
  type = 'button',
  ...props
}: ButtonProps): React.JSX.Element {
  return <button type={type} className={buttonClasses(variant, size, className)} {...props} />;
}

export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/** Même apparence que `Button`, pour les actions qui sont réellement des liens (export, retour). */
export function LinkButton({
  variant = 'secondary',
  size = 'default',
  className = '',
  ...props
}: LinkButtonProps): React.JSX.Element {
  return <a className={buttonClasses(variant, size, className)} {...props} />;
}
